import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../../Firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import {
  setDoc,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  collectionGroup,
  where,
  query,
  writeBatch,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

//Provide context for Authentication and Login/Alert Dialogs handling
const AuthContext = createContext();

//Context to manage authentication, loading Login/Signup Alert
export const AuthProvider = ({ children }) => {
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [isBookmarkForAlert, setIsBookmarkForAlert] = useState(false);
  const [bookmarks, setBookmarks] = useState({}); // New state for bookmarks
  const [mySavedPosts, setMySavedPosts] = useState([]); // New state for saved posts
  const [userPosts, setUserPosts] = useState([]); // New state for user posts

  const [userBio, setUserBio] = useState('');
  const [userLink, setUserLink] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsersname] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [photo, setPhoto] = useState('');
  const [bannerImage, setBannerImage] = useState('');

  const [userData, setUserData] = useState(null); // Add state to store user data
   // Add state for the verification snackbar:
   const [verificationSnackbar, setVerificationSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });
    // New flag to prevent showing the snackbar during sign-up
    const [skipVerificationSnackbar, setSkipVerificationSnackbar] = useState(false);

  const storage = getStorage(); // Initialize storage

  // Function to update AuthContext with new user data to be used in loginDialog
  const updateAuthContext = (userData) => {
    setCurrentUser((prev) => ({
      ...prev,
      ...userData, // Merge existing data with the new user data
   
    }));
    setUserData(userData); // Set the complete user data in a dedicated state
    // If setUserData is simply duplicating currentUser, consider if it’s needed. If it’s used separately (e.g.,
    //for API payloads or non-reactive purposes), keep it. Otherwise, remove it.
  };

  const updateUserPost = async (postId, updatedData) => {
    try {
      const postRef = doc(db, 'posts', postId); // Adjust path if needed
      await updateDoc(postRef, updatedData);
      //console.log('Post updated successfully');
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  };

  // Function to update user profile
  const updateUserProfile = async ({
    displayName,
    usersname,
    link,
    bio,
    photo,
    bannerImage,
    // profileImage,
  }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user.');
      return;
    }
    try {
      const userRef = doc(db, 'users', currentUser.uid); //Prepare the Firestore Update Data
      const updateData = {
        displayName,
        username: usersname,
        link,
        bio,
        ...(photo && { photo }), //new edit
        ...(bannerImage && { bannerImage }), //new edit
      };
      // if (photo) {
      //   //updateData.profileImage = profileImage;
      //   updateData.photo = photo; //update original user photoURL also with new Photo
      // }
      // if (bannerImage) {
      //   updateData.bannerImage = bannerImage;
      // }

      // Update the user's profile in Firestore
      await setDoc(userRef, updateData, { merge: true });

      // Find and update all posts by the user in the "posts" collection with new userInfo update
      const postsQuery = query(
        collection(db, 'posts'),
        where('userId', '==', currentUser.uid)
      );
      const postsSnapshot = await getDocs(postsQuery);
      // Update user's info in "savedPosts" collection
      const savedPostsQuery = query(
        collection(db, 'savedPosts'),
        where('userId', '==', currentUser.uid)
      );
      const savedPostsSnapshot = await getDocs(savedPostsQuery);

      // Update user's info in "comments" collection
      const commentsQuery = query(
        collection(db, 'comments'),
        where('userId', '==', currentUser.uid)
      );
      const commentsSnapshot = await getDocs(commentsQuery);

      if (!postsSnapshot.empty) {
        const batch = writeBatch(db);
        postsSnapshot.forEach((postDoc) => {
          const postRef = doc(db, 'posts', postDoc.id);
          const postUpdateData = {
            displayName,
            username: usersname,
            link,
            bio,
          };
          if (photo) {
            postUpdateData.userPhotoURL = photo; // Update userPhotoURL in posts
          }
          if (bannerImage) {
            postUpdateData.bannerImage = bannerImage;
          }
          batch.update(postRef, postUpdateData);
        });

        savedPostsSnapshot.forEach((savedPostDoc) => {
          const savedPostData = savedPostDoc.data(); // Get the data of the saved post
          const savedPostRef = savedPostDoc.ref;

          // Update only if the `uniqueId` of the saved post matches the current user's ID
          if (savedPostData.creatorsUserId === currentUser.uid && photo) {
            batch.update(savedPostRef, { photoURL: photo });
          }
        });

        // Update "comments" collection
        commentsSnapshot.forEach((commentDoc) => {
          const commentRef = commentDoc.ref;
          if (photo) {
            batch.update(commentRef, { userPhotoURL: photo });
          }
        });

        await batch.commit();

        // **Update AuthContext with new values**
        const updatedUserData = {
          displayName,
          username: usersname,
          link,
          bio,
          photo: photo || currentUser.photoURL,
          bannerImage: bannerImage || bannerImage, // Ensure fallback
          //profileImage: profileImage || profileImage, // Ensure fallback
        };
        updateAuthContext(updatedUserData);
        //console.log('Batch update successful');
      } else {
        console.log('No posts found for this user.');
      }

      // Update the user object in the context / Update AuthContext
      setCurrentUser((prev) => ({
        ...prev,
        displayName,
        username: usersname,
        link,
        bio,
        ...(photo && { photoURL: photo }),
        ...(bannerImage && { bannerImage }), // Add bannerImage
      }));
    } catch (error) {
      console.error('Error updating user profile and posts:', error);
    }
  };

  // Function to update photo & profileImage or banner image
  const updateUserImage = async (type, downloadURL) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user.');
      return;
    }
    try {
      // Update in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const updateData =
        type === 'photo'
          ? { photo: downloadURL } // Use only `photo` key
          : { [type]: downloadURL };

      // Update in Firestore
      await setDoc(userRef, updateData, { merge: true });
      // await setDoc(
      //   userRef,
      //   { [type]: downloadURL }, // Either profileImage or bannerImage
      //   { merge: true } // Avoid overwriting other fields
      // );

      // Update related posts if photo was updated
      if (type === 'photo') {
        const postsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', currentUser.uid)
        );
        const postsSnapshot = await getDocs(postsQuery);

        if (!postsSnapshot.empty) {
          const batch = writeBatch(db);
          postsSnapshot.forEach((postDoc) => {
            const postRef = doc(db, 'posts', postDoc.id);
            batch.update(postRef, { userPhotoURL: downloadURL });
          });
          await batch.commit();
         // console.log('Updated all posts with new profile image.');
        } else {
          console.log('No posts found for this user.');
        }
      }

      // Update in AuthContext
      setCurrentUser((prev) => ({
        ...prev,
        // [type === 'profileImage' ? 'photoURL' : 'bannerURL']: downloadURL,
        [type === 'photo' ? 'photo' : 'bannerImage']: downloadURL,
      }));

      //console.log(`${type} updated successfully in Firestore and AuthContext.`);
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
    }
  };

  const setUserProfile = ({
    displayName,
    username,
    userBio,
    userLink,
    photo,
    bannerImage,
    //profileImage,
  }) => {
    setDisplayName(displayName);
    setUsersname(username);
    setUserBio(userBio);
    setUserLink(userLink);
    setPhoto(photo);
    setBannerImage(bannerImage);
    // setProfileImage(profileImage); // replaced with photo
  };

  const openLogInModal = () => {
    setLoginOpen(true);
  };
  const handleLoginClose = () => {
    setLoginOpen(false);
    //If the drawer is open, close it
    if (openDrawer) {
      setOpenDrawer(false);
    }
  };
  //CloseAlert Dialog/modal
  const handleCloseAlert = () => {
    setOpenAlert(false);
    //Add that if the drawer is open, close it
    if (openDrawer) {
      setOpenDrawer(false);
    }
  };

  //SAVING BOOKMARKS IN STATE TO FETCH ALL USERS's BOOKMARKS
  useEffect(() => {
    if (currentUser) {
      // Fetch user's saved posts from Firebase on login
      const fetchBookmarks = async () => {
        const q = query(
          collection(db, 'savedPosts'),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);

        const bookmarksData = {};
        const savedPostsData = []; // Array to hold saved posts

        querySnapshot.forEach((doc) => {
          bookmarksData[doc.data().postId] = true;
          savedPostsData.push({ id: doc.id, ...doc.data() }); // Collect saved posts
        });

        setBookmarks(bookmarksData);
        setMySavedPosts(savedPostsData); // Set saved posts in state
      };
      // Fetch user's posts
      const fetchUserPosts = async () => {
        const q = query(
          collection(db, 'posts'),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);
        const userPostsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserPosts(userPostsData); // Set user posts in state
      };

      fetchBookmarks();
      fetchUserPosts();
    } else {
      setBookmarks({}); // Reset bookmarks when user logs out
      setMySavedPosts([]); // Reset saved posts when user logs out
      setUserPosts([]); // Reset user posts when user logs out
    }
  }, [currentUser]);

  const deleteUserPost = (postId) => {
    setUserPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));
  };

  const toggleBookmark = async (postID, postDetails) => {
    if (!currentUser) return;
    // const docRef = doc(db, 'savedPosts', `${currentUser.uid}_${postID}`);
    const docRef = doc(db, 'savedPosts', `${postID}`);
    const isCurrentlyBookmarked = bookmarks[postID];

    if (isCurrentlyBookmarked) {
      await deleteDoc(docRef); // selete the bookmarked post
    } else {
      await setDoc(docRef, {
        // save the post with userId included
        userId: currentUser.uid, // Ensure userId is included for rules validation
        postId: postID,
        //creatorsUserId: postDetails.userId, // The ID of the person who created the post
        ...postDetails, //creatorsUserId now included in postDetails
      });
    }

    // Update local state or the UI
    setBookmarks((prev) => ({
      ...prev,
      [postID]: !isCurrentlyBookmarked,
    }));
    // Update savedPosts state accordingly
    if (isCurrentlyBookmarked) {
      setMySavedPosts((prev) => prev.filter((post) => post.id !== postID)); // Remove from saved posts
    } else {
      setMySavedPosts((prev) => [...prev, { id: postID, ...postDetails }]); // Add to saved posts
    }
  };

  //to retrieve user data and update it with current one
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user); // Retain the raw user object from Firebase Auth
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          //updateAuthContext(userData); // Update Authcontext with fetched user data
          updateAuthContext({ ...userData, providerData: user.providerData }); // Include providerData
        } else {
          console.error('User document not found in Firestore');
        }
      } else {
        setCurrentUser(null);
        setUserData(null); // Reset user data on logout
      }
      setLoading(false); // Ensure loading state is updated
    });

    return () => unsubscribe();
  }, []);

  // //if a user is signed in but has not verified their email, you immediately sign them out
  // useEffect(() => {
  //   const unsubscribe = onAuthStateChanged(auth, async (user) => {
  //     if (user) {
  //       await user.reload(); // Refresh user data
  //       if (!user.emailVerified && !skipVerificationSnackbar) {
  //         // If email is not verified, sign them out and maybe show a message
  //         // Set a snackbar message and sign out
  //         setVerificationSnackbar({
  //           open: true,
  //           message: "Please verify your email to sign in.",
  //           severity: "warning",
  //         });
  //         await signOut(auth);
  //         // Optionally: you can set a state to show a message like "Please verify your email to sign in."
  //         setLoading(false);
  //         return;
  //       }
  //       // If verified, set the current user
  //       setCurrentUser(user);
  //       setLoading(false);
  //     } else {
  //       setCurrentUser(null);
  //       setLoading(false);
  //     }
  //   });
  //   return () => unsubscribe();
  // }, []);
  



  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userData,
        setUserProfile,
        updateAuthContext,
        setCurrentUser,
        setDisplayName,
        setUsersname,
        setUserBio,
        setUserLink,
        updateUserProfile,
        updateUserImage,
        userBio,
        userLink,
        displayName,
        username, //pass username down
        loading,
        setLoading,
        loginOpen,
        setLoginOpen,
        openLogInModal,
        handleLoginClose,
        handleCloseAlert,
        showAlertDialog,
        setShowAlertDialog,
        openDrawer,
        setOpenDrawer,
        isBookmarkForAlert,
        setIsBookmarkForAlert,
        bookmarks, // Provide the bookmarks state
        toggleBookmark, // Provide the toggleBookmark function
        mySavedPosts, // Provide the saved posts state
        mySavedPostsLength: mySavedPosts.length, // Provide the length of saved posts
        userPosts, // Provide the user posts state
        userPostsLength: userPosts.length, // Provide the length of user posts
        deleteUserPost,
        updateUserPost,
        verificationSnackbar,
        setVerificationSnackbar,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
